import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { BLOCK, DIST_STATUS, DIST_TYPE, distStatusOption, FISCAL_YEAR, FROM_MONTH, FROM_YEAR, GL_FILTER_MODE, GL_MASTER_IDS, MONTH_RANGE, PROPERTY, RECORD_TYPE, REPORTING_LEVEL, summaryCards, TO_MONTH, TO_YEAR, ZONE } from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema } from "./validation";
import { useForm, useWatch } from "react-hook-form";
import { allOption, cn, formatAmount, getRequiredFields } from "@/lib/utils";
import { useGetCommunityFinancialInfo, useGetCostTrialBalanceReport, useGetGlMasterData, useGetPropertiesByCommunity, useGetPropertyBlockDropdown, useGetPropertyZoneDropdown } from "@/services";
import moment from "moment";
import { Info } from "lucide-react";
import { Button } from "@/components/ui";

const useTrialBalance = () => {
  const {communityData, currentFinanceYear} = useSelector((state: RootState) => state.currentData);
  const communityName = communityData?.name || communityData?.communityName || "";
  const [openTabs, setOpenTabs] = useState<string[]>(["filter"]);
  const [openGroupGlDialog, setOpenGroupGlDialog] = useState<boolean>(false);
  const [isDistributed, setIsDistributed] = useState<boolean>(false);
  const [selectedZoneId, setSelectedZoneId] = useState<string>("");
  const isRunClick = useRef<boolean>(false);
  const groupGlList = useRef<any>(null);
  const[openDocumentDialog, setOpenDocumentDialog] = useState<boolean>(false);
  const selectedDocument = useRef<any>(null);
  
  const form = useForm<any>({
    defaultValues: {
      [MONTH_RANGE]: {
        from: "",
        to: ""
      },
      [FISCAL_YEAR]: currentFinanceYear,
      [REPORTING_LEVEL]: "",
      [ZONE]: ["all"],
      [BLOCK]: ["all"],
      [PROPERTY]: ["all"],
      [DIST_STATUS]: [],
      [DIST_TYPE]: [],
      [RECORD_TYPE]: "all",
      [GL_MASTER_IDS]: []
    },
    resolver: zodResolver(schema)
  });
  
  const reportingLevel = useWatch({control: form.control, name: REPORTING_LEVEL});

  const formData = useWatch({control: form.control});

  const requiredFields = getRequiredFields(schema);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: "",
        name: <span className="text-primary">Trial Balance</span>,
      },
    ],
    [communityName]
  );

  const onAccordionChange = (e: any) => {
    setOpenTabs([e[e.length - 1]]);
  };
  
  const {
    data: communityFinancialInfo,
    isLoading: isFinancialInfoLoading,
  } = useGetCommunityFinancialInfo({
    community_id: communityData?.communityId || "",
    queryParams: {
      enabled: !!(communityData?.communityId),
    },
  });

  useEffect(() => {
    if (communityFinancialInfo) {
      form.setValue(REPORTING_LEVEL, communityFinancialInfo?.data?.distributionLevel);
    }
  }, [communityFinancialInfo]);

  const { data: propertyZones, isLoading: isLoadingZones } =
    useGetPropertyZoneDropdown({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  const { data: propertyBlock, isLoading: isLoadingBlocks } =
    useGetPropertyBlockDropdown({
      communityId: communityData?.communityId,
      zoneId: selectedZoneId,
      queryParams: {
        enabled: !!communityData?.communityId
      }
    });

  const { data: propertyList, isLoading: isLoadingProperty } =
    useGetPropertiesByCommunity({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: !!communityData?.communityId
      }
    });

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId,
    },
  });

  const glOptions = useMemo(() =>
    (glData?.data || [])
      .filter((data: any) => data?.accountCategory == "I" || data?.accountCategory == "E")
      .map((data: any) => ({
        value: data?.glMasterId?.toString(),
        label: `${data?.glAccountNumber || ""} - ${data?.accountShortDesc || ""}`,
      })),
    [glData]
  );

  const isAllSelected = (value: any) =>
    Array.isArray(value) ? value.includes("all") : value == "all";

  const { data: reportData, isRefetching: isLoadingReportData, refetch: refetchReportData } =
    useGetCostTrialBalanceReport({
      communityId: communityData?.communityId,
      filter: {
        ...(formData[MONTH_RANGE]?.from && formData[MONTH_RANGE]?.to ? {
          [FROM_YEAR]: moment(formData[MONTH_RANGE]?.from).format("YYYY"),
          [TO_YEAR]: moment(formData[MONTH_RANGE]?.to).format("YYYY"),
          [FROM_MONTH]: moment(formData[MONTH_RANGE]?.from).format("MM"),
          [TO_MONTH]: moment(formData[MONTH_RANGE]?.to).format("MM"),
        } : {}),
        [FISCAL_YEAR]: formData[FISCAL_YEAR],
        [GL_FILTER_MODE]: isDistributed ? "DISTRIBUTED_ONLY" : "ALL",
        ...(formData[DIST_STATUS]?.length ? {[DIST_STATUS]: formData[DIST_STATUS]} : {}),
        ...(formData[DIST_TYPE]?.length ? {[DIST_TYPE]: formData[DIST_TYPE]} : {}),
        ...(formData?.[RECORD_TYPE] == "all" ? {} : {[RECORD_TYPE]: formData[RECORD_TYPE]}),
        ...(isAllSelected(formData?.[ZONE]) ? {} : {[ZONE]: formData[ZONE]}),
        ...(isAllSelected(formData?.[BLOCK]) ? {} : {[BLOCK]: formData[BLOCK]}),
        ...(isAllSelected(formData?.[PROPERTY]) ? {} : {[PROPERTY]: formData[PROPERTY]}),
        ...(formData[GL_MASTER_IDS]?.length ? { [GL_MASTER_IDS]: formData[GL_MASTER_IDS] } : {}),
      },
      queryParams: {
        enabled: false
      }
    });
  
  const isZone = useMemo(() => reportingLevel?.toLowerCase() == "zone", [reportingLevel]);
  const isBlock = useMemo(() => reportingLevel?.toLowerCase() == "block", [reportingLevel]);
  const isProperty = useMemo(() => reportingLevel?.toLowerCase() == "property", [reportingLevel]);

  const updatedSummaryCards = useMemo(() => {
    const updatedCards = summaryCards?.map((data: any) => ({
      ...data,
      children: data?.children?.map((child: any) => ({...child, amt: reportData?.data?.kpis?.[child.key]})),
      amt: formatAmount(reportData?.data?.kpis?.[data.amtKey] || "0"),
      desc: data?.descKey ? `${reportData?.data?.kpis?.[data.descKey] ? "Balanced" : "Unbalanced"}` : data?.desc,
      isBalanced: reportData?.data?.kpis?.[data.descKey],
    }));

    return updatedCards;
  }, [reportData]);

  const onClear = (e: any) => {
    e.preventDefault();
    form.reset({
      [MONTH_RANGE]: {
        from: "",
        to: ""
      },
      [FISCAL_YEAR]: currentFinanceYear,
      [REPORTING_LEVEL]: reportingLevel,
      [ZONE]: ["all"],
      [BLOCK]: ["all"],
      [PROPERTY]: ["all"],
      [DIST_STATUS]: [],
      [DIST_TYPE]: [],
      [RECORD_TYPE]: "",
      [GL_MASTER_IDS]: []
    })
    isRunClick.current = false;
  }

  const onSubmit = () => {
    isRunClick.current = true;
    refetchReportData();
  }

  const columns = [
    {
      accessorKey: "costObjectName",
      header: <div>Account / Cost Object</div>,
      cell: ({row}: any) => <div className="flex items-center flex-row gap-0">
        <span>{row?.original?.costObjectName}</span>
      </div>,
    },
    {
      accessorKey: "primaryDr",
      header: <div>Primary<br/>Posting DR</div>,
      accessorFn: (row: any) => formatAmount(row?.primaryDr || "0", 2),
    },
    {
      accessorKey: "primaryCr",
      header: <div>Primary<br/>Posting CR</div>,
      accessorFn: (row: any) => formatAmount(row?.primaryCr || "0", 2),
    },
    {
      accessorKey: "secondaryDr",
      header: <div>Secondary<br/>Posting DR</div>,
      accessorFn: (row: any) => formatAmount(row?.secondaryDr || "0", 2),
    },
    {
      accessorKey: "secondaryCr",
      header: <div>Secondary<br/>Posting CR</div>,
      accessorFn: (row: any) => formatAmount(row?.secondaryCr || "0", 2),
    },
    {
      accessorKey: "unallocated",
      header: <div>Unallocated</div>,
      accessorFn: (row: any) => formatAmount(row?.unallocatedDr || "0", 2),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: ({row}: any) => {
        const selected = distStatusOption?.find((data: any) => data?.value == row?.original?.status)
        return <span className={cn(selected?.className)}>{selected?.label || ""}</span>
      },
    },
  ];

  const subColumns = [
    {
      accessorKey: "glAccountNumber",
      header: <div>Account / Cost Object</div>,
      cell: (row: any) => <div className="flex items-center flex-row gap-0">
        <span>GL - {row?.glAccountNumber} {String(row?.glName)}</span>
        {row?.groupGl ? 
          <Button className="p-0" variant="ghost" 
            onClick={(e: any) => {
              e.preventDefault();
              setOpenGroupGlDialog(true);
              groupGlList.current = row?.groupMembers;
            }}
          >
            <Info className="h-4 w-4 stroke-primary" />
          </Button> : <></>
        }
      </div>,
    },
    {
      accessorKey: "primaryDr",
      header: <div>Primary<br/>Posting DR</div>,
      accessorFn: (row: any) => formatAmount(row?.primaryDr || "0", 2),
    },
    {
      accessorKey: "primaryCr",
      header: <div>Primary<br/>Posting CR</div>,
      accessorFn: (row: any) => formatAmount(row?.primaryCr || "0", 2),
    },
    {
      accessorKey: "secondaryDr",
      header: <div>Secondary<br/>Posting DR</div>,
      accessorFn: (row: any) => formatAmount(row?.secondaryDr || "0", 2),
    },
    {
      accessorKey: "secondaryCr",
      header: <div>Secondary<br/>Posting CR</div>,
      accessorFn: (row: any) => formatAmount(row?.secondaryCr || "0", 2),
    },
    {
      accessorKey: "unallocated",
      header: <div>Unallocated</div>,
      accessorFn: (row: any) => formatAmount(row?.unallocatedDr || "0", 2),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: ({row}: any) => {
        const selected = distStatusOption?.find((data: any) => data?.value == row?.original?.status)
        return <span className={cn(selected?.className)}>{selected?.label || ""}</span>
      },
    },
  ];

  const level2Column = [
    {
      accessorKey: "costObjectName",
      header: <div>Account / Cost Object</div>,
      cell: (row: any) => {
        return row?.postDocumentHeaderId ? (
          <Button variant="link" className="px-0" onClick={(e: any) => {
            e.preventDefault();
            e.stopPropagation();
            selectedDocument.current = {...row, postDocHeaderId: row?.postDocumentHeaderId, communityId: communityData?.communityId};
            setTimeout(() => setOpenDocumentDialog(true), 200);
          }}>{row?.costObjectName}</Button>
      ) : <span>{row?.costObjectName}</span>
    },
    },
    {
      accessorKey: "primaryDr",
      header: <div>Primary<br/>Posting DR</div>,
      accessorFn: (row: any) => formatAmount(row?.primaryDr || "0", 2),
    },
    {
      accessorKey: "primaryCr",
      header: <div>Primary<br/>Posting CR</div>,
      accessorFn: (row: any) => formatAmount(row?.primaryCr || "0", 2),
    },
    {
      accessorKey: "secondaryDr",
      header: <div>Secondary<br/>Posting DR</div>,
      accessorFn: (row: any) => formatAmount(row?.secondaryDr || "0", 2),
    },
    {
      accessorKey: "secondaryCr",
      header: <div>Secondary<br/>Posting CR</div>,
      accessorFn: (row: any) => formatAmount(row?.secondaryCr || "0", 2),
    },
    {
      accessorKey: "unallocated",
      header: <div>Unallocated</div>,
      accessorFn: (row: any) => formatAmount(row?.unallocatedDr || "0", 2),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: ({row}: any) => {
        const selected = distStatusOption?.find((data: any) => data?.value == row?.original?.status)
        return <span className={cn(selected?.className)}>{selected?.label || ""}</span>
      },
    },
  ];

  const onDisTypeChange = (value: any) => (checked: boolean) => {
    let distType = [...formData[DIST_TYPE]];

    if (checked) {
      distType.push(value);
    } else {
      distType = distType.filter((val: any) => val != value);
    }
    form.setValue(DIST_TYPE, distType);
  }

  const onDisStatusChange = (value: any) => (checked: boolean) => {
    let distStatus = [...formData[DIST_STATUS]];

    if (checked) {
      distStatus.push(value);
    } else {
      distStatus = distStatus.filter((val: any) => val != value);
    }
    form.setValue(DIST_STATUS, distStatus);
  }

  const onRecordTypeChange = (value: any) => {
    form.setValue(RECORD_TYPE, value);
  }

  const onZoneChange = (value: any) => {
    setSelectedZoneId(value?.includes("all") ? "" : value?.toString());
    form.setValue(BLOCK, ["all"]);
    form.setValue(PROPERTY, ["all"]);
  }

  const onPropertyChange = (value: any) => {
    if (value?.includes("all")) {
      form.setValue(BLOCK, ["all"]);
      form.setValue(ZONE, ["all"]);
      return;
    }

    const blockIds = [...new Set((propertyList?.data || [])
      ?.filter((data: any) => value?.includes(data?.propertyId?.toString()))
      ?.map((data: any) => data?.blockId?.toString())
      ?.filter(Boolean))];

    const zoneIds = [...new Set((propertyList?.data || [])
      ?.filter((data: any) => blockIds?.includes(data?.blockId?.toString()))
      ?.map((data: any) => data?.zoneId?.toString())
      ?.filter(Boolean))];

    form.setValue(BLOCK, blockIds?.length ? blockIds : ["all"]);
    if (zoneIds?.length) {
      form.setValue(ZONE, zoneIds);
    }
  }

  const onBlockChange = (value: any) => {
    if (value?.includes("all")) {
      form.setValue(ZONE, ["all"]);
      form.setValue(PROPERTY, ["all"]);
      return;
    }

    const zoneIds = [...new Set((propertyList?.data || [])
      ?.filter((data: any) => value?.includes(data?.blockId?.toString()))
      ?.map((data: any) => data?.zoneId?.toString())
      ?.filter(Boolean))];

    if (zoneIds?.length) {
      form.setValue(ZONE, zoneIds);
    }
    form.setValue(PROPERTY, ["all"]);
  }

  const blockList = useMemo(() => {
    const options = (propertyBlock?.data || [])?.map((val: any) => ({
      label: val?.name?.toString(),
      value: val?.id?.toString(),
    }))

    return [...allOption, ...options]
  }, [propertyBlock]);

  const filteredPropertyList = useMemo(() => {
    if (isAllSelected(formData?.[BLOCK])) {
      return propertyList?.data || [];
    }

    return (propertyList?.data || [])?.filter((data: any) =>
      formData?.[BLOCK]?.includes(data?.blockId?.toString())
    );
  }, [propertyList, formData?.[BLOCK]]);

  const onGlFilterModeChange = () => {
    setIsDistributed((prev: boolean) => !prev);
    setTimeout(() => refetchReportData(), 100);
  }

  return {
    breadcrumbData,
    onAccordionChange,
    openTabs,
    form,
    requiredFields,
    isFinancialInfoLoading,
    propertyZones,
    propertyBlock,
    isLoadingZones,
    isLoadingBlocks,
    propertyList,
    filteredPropertyList,
    isZone,
    isBlock,
    isProperty,
    isLoadingProperty,
    formData,
    updatedSummaryCards,
    isLoadingReportData,
    onClear,
    onSubmit,
    columns,
    subColumns,
    tableData: reportData?.data?.rows || [],
    isRunClick: isRunClick.current,
    onDisTypeChange,
    onDisStatusChange,
    onRecordTypeChange,
    onZoneChange,
    onBlockChange,
    onPropertyChange,
    currentFinanceYear,
    blockList,
    openGroupGlDialog,
    setOpenGroupGlDialog,
    groupGlList: groupGlList.current,
    isDistributed,
    onGlFilterModeChange,
    openDocumentDialog,
    setOpenDocumentDialog,
    selectedDocument: selectedDocument.current,
    level2Column,
    glOptions,
    isLoadingGlMaster
  }
}

export default useTrialBalance;
